import { type RefObject, useEffect, useRef } from "react";
import { translations } from "~/services/translations/translations";
import type { StepStepper, NavItem } from "./types";
import { arrayIsNonEmpty } from "~/util/array";
import {
  stateIsCurrent,
  stateIsActive,
  stateIsWarning,
} from "~/services/navigation/navState";
import { getMobileButtonAreaTitles } from "~/components/navigation/getMobileButtonAreaTitles";
import classNames from "classnames";
import { Icon } from "../common/Icon";
import { NavigationList } from "./NavigationList";

const DATA_TESTID_STEP_STEPPER_LINK = "step-stepper-link";

const StepStepperLinks = ({
  stepsStepper,
}: Readonly<{
  stepsStepper: StepStepper[];
}>) => {
  if (!arrayIsNonEmpty(stepsStepper)) {
    return null;
  }

  return (
    <>
      {stepsStepper
        .map((step, index) => {
          return { ...step, stepIndex: index + 1 };
        })
        .filter(
          (steps) => !stateIsCurrent(steps.state) && stateIsActive(steps.state),
        )
        .map((step) => {
          const isWarningStep = stateIsWarning(step.state);
          return (
            <div className="flex flex-row pl-16 pr-0 pb-16" key={step.label}>
              <a
                href={step.href}
                className="flex text-ellipsis max-w-[80vw] kern-link text-left pt-0!"
                data-testid={DATA_TESTID_STEP_STEPPER_LINK}
                aria-describedby={isWarningStep ? step.href : undefined}
              >
                <Icon name="arrow-back" className="w-[1.2em] h-[1.2em] mt-2" />
                {`${translations.navigationMobile.toStep.de} ${step.label} (${step.stepIndex}/${stepsStepper.length})`}
              </a>
              {isWarningStep && (
                <Icon
                  name="warning"
                  className="pl-2 fill-kern-feedback-warning!"
                  ariaLabel={translations.navigation.navigationItemWarning.de}
                />
              )}
            </div>
          );
        })}
    </>
  );
};

const initializeKeyDownListeners = (
  summaryRef: RefObject<HTMLElement | null>,
  selector: string,
) => {
  const summary = summaryRef.current;
  const details = summary?.parentElement as HTMLDetailsElement | null;
  const links = document.querySelectorAll<HTMLAnchorElement>(selector);
  const lastLink = links[links.length - 1];

  if (!lastLink || summary === null || links.length === 0 || details === null) {
    return;
  }

  const handleEscape = (event: KeyboardEvent) => {
    if (event.key === "Escape" && details.open) {
      event.preventDefault();
      details.open = false;
      summary.focus();
    }
  };
  const handleSummaryKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Tab" && event.shiftKey && details.open) {
      event.preventDefault();
      lastLink.focus();
    }
  };

  const handleLastLinkKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Tab" && !event.shiftKey) {
      event.preventDefault();
      summary.focus();
    }
  };

  document.addEventListener("keydown", handleEscape);
  summary.addEventListener("keydown", handleSummaryKeyDown);
  lastLink.addEventListener("keydown", handleLastLinkKeyDown);

  return () => {
    document.removeEventListener("keydown", handleEscape);
    summary.removeEventListener("keydown", handleSummaryKeyDown);
    lastLink.removeEventListener("keydown", handleLastLinkKeyDown);
  };
};

export default function SideNavMobile({
  navItems,
  stepsStepper,
}: Readonly<{
  navItems: NavItem[];
  stepsStepper: StepStepper[];
}>) {
  const firstItemRef = useRef<HTMLAnchorElement | null>(null);
  const summaryRef = useRef<HTMLElement | null>(null);

  const { currentAreaTitle, currentNavTitle } = getMobileButtonAreaTitles(
    navItems,
    stepsStepper,
  );

  const hasStepsStepperLinks = stepsStepper.some(
    (steps) => !stateIsCurrent(steps.state) && stateIsActive(steps.state),
  );
  const keyDownSelector = `[data-testid=${hasStepsStepperLinks ? DATA_TESTID_STEP_STEPPER_LINK : "nav-item-link"}]`;

  const isStateCurrentWarning = arrayIsNonEmpty(stepsStepper)
    ? stepsStepper.some(({ state }) => state === "WarningCurrent")
    : navItems.some(({ state }) => state === "WarningCurrent");

  useEffect(() => {
    initializeKeyDownListeners(summaryRef, keyDownSelector);
  });

  return (
    <details
      className="group flex flex-col outline-none! open:min-h-screen justify-end bg-transparent"
      data-testid="side-nav-details"
    >
      <summary
        className="flex flex-col cursor-pointer outline-none group/summary"
        data-testid="side-nav-summary"
        ref={summaryRef}
      >
        <div
          className="not-group-open:hidden min-h-screen flex bg-black opacity-70"
          data-testid="close-overlay"
        />
        <div
          className={classNames(
            "flex bg-white h-80 items-center py-8! px-16! z-10 flex-row justify-between border border-kern-neutral-200 not-group-open:active:bg-kern-neutral-200! overflow-hidden forced-colors:group-focus-within/summary:border-4 forced-colors:group-focus-within/summary:border-[CanvasText]",
            "group-focus-visible/summary:bg-white",
            "group-focus-visible/summary:shadow-[inset_0_0_0_2px_var(--kern-color-action-on-default),inset_0_0_0_4px_var(--kern-color-action-focus-default),inset_0_0_0_6px_var(--kern-color-action-focus-default)]",
            {
              "not-group-open:bg-kern-orange-100! not-group-open:active:bg-kern-orange-100!":
                isStateCurrentWarning,
            },
          )}
        >
          <div className="flex flex-row gap-8">
            <div className="flex flex-col items-start">
              <p className="kern-body--bold truncate text-left w-[70vw]">
                <span className="sr-only">
                  {translations.navigationMobile.toggleMenu.de}
                </span>
                {currentAreaTitle}
              </p>
              <p className=" text-kern-layout-text-muted">{currentNavTitle}</p>
            </div>
          </div>
          <Icon
            name="keyboard-arrow-up"
            className="hidden! group-open:block! fill-kern-action-default"
          />
          <Icon
            name="keyboard-arrow-down"
            className="block! group-open:hidden! fill-kern-action-default"
          />
        </div>
      </summary>
      <div className="max-h-[80vh] bg-white overflow-auto">
        <div className="flex flex-col p-16">
          <NavigationList
            navItems={navItems}
            className="border border-kern-neutral-200 rounded-sm"
            firstItemRef={firstItemRef}
          />
        </div>
        <StepStepperLinks stepsStepper={stepsStepper} />
      </div>
    </details>
  );
}
